// src/firebase/markerComments.js
// Marker Personal Comment Bank - each marker's own reusable feedback comments.
// Separate from the shared commentBank (comments.js): no suggest/approve step,
// a marker's own comments are theirs alone to add/edit/delete.

import {
  doc,
  getDoc,
  setDoc
} from 'firebase/firestore';
import { db } from './config';

const bankRef = (markerId, homeworkType) =>
  doc(db, 'markerCommentBanks', markerId, 'banks', homeworkType);

const emptyBank = () => ({
  sectionA: [],
  sectionB: [],
  sectionC: [],
  final: [],
  finalQuestions: {}
});

const sectionKeyFor = (section) => (section === 'final' ? 'final' : `section${section}`);

/**
 * Get a marker's personal comment bank for a homework type
 */
export const getMyCommentBank = async (markerId, homeworkType) => {
  try {
    const docSnap = await getDoc(bankRef(markerId, homeworkType));
    return docSnap.exists() ? docSnap.data() : emptyBank();
  } catch (error) {
    console.error('Get my comment bank error:', error);
    throw error;
  }
};

/**
 * Add a comment to the marker's own bank (no approval needed)
 */
export const addMyComment = async (markerId, homeworkType, section, commentText, header = null) => {
  try {
    const docRef = bankRef(markerId, homeworkType);
    const comment = {
      id: `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      text: commentText,
      header: header || null,
      createdAt: new Date().toISOString()
    };

    const docSnap = await getDoc(docRef);
    const currentData = docSnap.exists() ? docSnap.data() : emptyBank();

    const sectionKey = sectionKeyFor(section);
    const existing = currentData[sectionKey];
    if (existing && !Array.isArray(existing) && typeof existing === 'object') {
      currentData[sectionKey] = {
        ...existing,
        comments: [...(existing.comments || []), comment]
      };
    } else {
      currentData[sectionKey] = [...(existing || []), comment];
    }

    await setDoc(docRef, currentData);
    return comment;
  } catch (error) {
    console.error('Add my comment error:', error);
    throw error;
  }
};

/**
 * Add a comment to the marker's own per-question final comment bank
 * (finalQuestions is keyed by questionId, not by the section letter/'final' scheme
 * the other banks use, so it needs its own read/write path)
 */
export const addMyFinalQuestionComment = async (markerId, homeworkType, questionId, commentText) => {
  try {
    const docRef = bankRef(markerId, homeworkType);
    const comment = {
      id: `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      text: commentText,
      createdAt: new Date().toISOString()
    };

    const docSnap = await getDoc(docRef);
    const currentData = docSnap.exists() ? docSnap.data() : emptyBank();
    const existing = currentData.finalQuestions || {};
    currentData.finalQuestions = {
      ...existing,
      [questionId]: [...(existing[questionId] || []), comment]
    };

    await setDoc(docRef, currentData);
    return comment;
  } catch (error) {
    console.error('Add my final question comment error:', error);
    throw error;
  }
};

/**
 * Update one of the marker's own comments
 */
export const updateMyComment = async (markerId, homeworkType, section, commentId, newText, header = null) => {
  try {
    const docRef = bankRef(markerId, homeworkType);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) throw new Error('Comment bank not found');

    const data = docSnap.data();
    const sectionKey = sectionKeyFor(section);
    const existing = data[sectionKey];
    const isNewFormat = existing && !Array.isArray(existing) && typeof existing === 'object';
    const comments = isNewFormat ? (existing.comments || []) : (existing || []);
    const updatedComments = comments.map(comment =>
      comment.id === commentId
        ? { ...comment, text: newText, header: header || null, updatedAt: new Date().toISOString() }
        : comment
    );
    data[sectionKey] = isNewFormat ? { ...existing, comments: updatedComments } : updatedComments;
    await setDoc(docRef, data);
  } catch (error) {
    console.error('Update my comment error:', error);
    throw error;
  }
};

/**
 * Delete one of the marker's own comments
 */
export const deleteMyComment = async (markerId, homeworkType, section, commentId) => {
  try {
    const docRef = bankRef(markerId, homeworkType);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) return;

    const data = docSnap.data();
    const sectionKey = sectionKeyFor(section);
    const existing = data[sectionKey];
    const isNewFormat = existing && !Array.isArray(existing) && typeof existing === 'object';
    const comments = isNewFormat ? (existing.comments || []) : (existing || []);
    const filteredComments = comments.filter(comment => comment.id !== commentId);
    data[sectionKey] = isNewFormat ? { ...existing, comments: filteredComments } : filteredComments;
    await setDoc(docRef, data);
  } catch (error) {
    console.error('Delete my comment error:', error);
    throw error;
  }
};
